import { useContext } from 'react';
import * as React from 'react';
import merge from 'lodash.merge';
import z from 'zod';

import { PassengerTypeConfig } from './SearchForm/types';
import { Locale, Mode } from './types';
import { extendI18n, changeLocale, captureErrorSentry, removeTrailingSlash } from './utils';
import { setApiUrl } from './graphql-client';
import { StepsEnum } from './types';
import {
	LocationGroupType,
	LoginType,
	OauthServiceType,
	OrderAdditionalServiceGdsServiceServiceType,
	ExareStatus
} from '@websky/graphql';
import { Sorting } from './Results/enums';
import { CompanyInfoSchema, LoyaltyInfoLinkSchema, ReserveEnginesSchema } from './configSchema/GlobalConfigSchema';
import ConfigSchema from './configSchema/index';
import { applyServicesStepsOrderToDefaultSteps } from './Checkout/servicesStepsOrder';

export interface LoginMethod {
	service: LoginType;
	status: boolean;
	id: string;
	userName: string;
}

export type LoyaltyInfoLink = z.infer<typeof LoyaltyInfoLinkSchema>;

export type ReserveEngines = z.infer<typeof ReserveEnginesSchema>;

export type CompanyInfo = z.infer<typeof CompanyInfoSchema>;

export type Config = z.infer<typeof ConfigSchema> & {
	validate?: () => z.ZodIssue[];
};

export const defaultPassengersConfig: PassengerTypeConfig[] = [
	{
		code: 'ADT',
		isAdult: true,
		defaultCount: 1
	},
	{
		code: 'CLD',
		isAdult: false,
		maxCountWithoutAdult: 0
	},
	{
		code: 'INF',
		isAdult: false
	},
	{
		code: 'INS',
		isAdult: false
	}
];

export const defaultConfig: Config = {
	FlightStatus: {
		engineURL: '',
		appStoreURL: 'https://apps.apple.com/id/app/websky-app/id1567545689',
		playMarketURL: 'https://play.google.com/store/apps/details?id=aero.websky.demo',
		accessToken:
			'pk.eyJ1Ijoic2NhdC1haXJsaW5lcyIsImEiOiJjbHFieWNteHMwOTU0Mml0a3Y4bTFsYWxmIn0.5G_TN53qWDMx0aY3IeDVbw',
		starlineAccessToken:
			'eyJ0eXAiOiJKV1QiLCJhbGciOiJub25lIiwiZG9tYWluIjoiIn0.eyJpc3MiOiJodHRwczovL21hcHMuc3RhcmxpbmUucnUiLCJpYXQiOjE3MzkxODc5ODQuMDkxODE3LCJ1aWQiOjQ0fQ.',
		starlineStyleId: '5b54b75f-153f-44f7-bc2a-826acf291981',
		stylesEn: 'mapbox://styles/evgenylitvinenkomlsd/clq2mj6h301tk01pa3xaqg95c',
		stylesRu: 'mapbox://styles/evgenylitvinenkomlsd/clkv3s6c8001z01pd51do54s1'
	},
	SearchForm: {
		redwingsForeignCardPaymentCheckbox: false,
		maxComplexRouteSegments: 2,
		enableComplexRoute: true,
		mobileFormType: 'alternative',
		engineURL: '',
		payByMiles: false,
		showFfpBonusInfoInFares: false,
		requestCoordintates: {
			desktop: true,
			mobile: true
		},
		maxPassengersCount: 9,
		passengersConfig: defaultPassengersConfig,
		offerHotels: false,
		offerHotelsDefaultState: true,
		offerHotelsLink: 'https://ostrovok.ru/hotels',
		locationGroups: {
			showAdditionalGroups: true,
			additionalGroupTypes: [
				LocationGroupType.AdditionalCities,
				LocationGroupType.Nearest,
				LocationGroupType.Popular
			]
		},
		separatedDatepicker: false,
		additionalLinks: [],
		useFlightsSchedule: false,
		alwaysOpen: false
	},
	Engine: {
		searchFormURL: '',
		defaultSteps: {
			flights: {
				label: 'Flights'
			},
			passengers: {
				label: 'Passengers'
			},
			order: {
				label: 'Order'
			},
			payment: {
				label: 'Payment'
			}
		},
		checkInURL: '',
		accountURL: '',
		mealWarning: '',
		promoLoaders: [],
		simpleLoaders: [],
		checkInEnabled: false,
		checkFfpStatus: false,
		showRefundRedirectButton: false,
		showTicketRefundOptionModal: false,
		calendarDaysCount: 7,
		showAvailableFaresOnly: false,
		sortResultPreferCurrentAirline: false,
		resultsFareSelectorInModal: true,
		resultsFareSlidesToShow: {},
		ignoreBlockedPaymentToReturnOrder: true,
		googleOneTapSignInClientId: null,
		exareShowPassengersStep: true,
		resultsDefaultSorting: Sorting.Price,
		nas: {
			suggestServicesTypes: [
				OrderAdditionalServiceGdsServiceServiceType.Baggage,
				OrderAdditionalServiceGdsServiceServiceType.Seat,
				OrderAdditionalServiceGdsServiceServiceType.Meal,
				OrderAdditionalServiceGdsServiceServiceType.Misc
			],
			suggestedServicesOrder: {
				insurance: 0,
				insuranceGroups: 0,
				[OrderAdditionalServiceGdsServiceServiceType.Meal]: 1,
				[OrderAdditionalServiceGdsServiceServiceType.Baggage]: 2,
				[OrderAdditionalServiceGdsServiceServiceType.Seat]: 3,
				[OrderAdditionalServiceGdsServiceServiceType.Misc]: 4
			}
		},
		RTMinPricesForOWFlights: false,
		useActualizedOrderFlightPrices: true,
		refund: {
			successModal: {
				create: {
					[ExareStatus.InProcess]: true,
					[ExareStatus.NeedConfirm]: true,
					[ExareStatus.AwaitingPayment]: true,
					[ExareStatus.Finished]: true,
					[ExareStatus.Rejected]: true,
					[ExareStatus.Refused]: true,
					[ExareStatus.TimelimitExpired]: true
				},
				request: {
					[ExareStatus.InProcess]: true,
					[ExareStatus.NeedConfirm]: true,
					[ExareStatus.AwaitingPayment]: true,
					[ExareStatus.Finished]: true,
					[ExareStatus.Rejected]: true,
					[ExareStatus.Refused]: true,
					[ExareStatus.TimelimitExpired]: true
				}
			}
		},
		Exare: {
			provideConfirmDocuments: true
		}
	},
	Account: {
		engineURL: '',
		showUserPhoneInput: true,
		showCommunications: false,
		showSubscribeCheckbox: false,
		initialStayLoggedIn: false
	},
	Checkin: {
		CheckinConditions: ['OpenCloseCheckinCondition', 'BaggageCondition', 'PassengersCondition'],
		showConsents: false,
		checkinRestrictionsEnabled: false
	},
	global: {
		savePreselectedWithoutUserAction: {
			types: []
		},
		additionalResources: {
			scripts: ['https://secure.sirena-travel.ru/media/js/pg-additional-1.0.min.js'],
			extraScripts: [],
			svgs: ['https://cdn.websky.aero/content/ZZ/images/icons.svg'],
			extraSvgs: []
		},
		socialAuthMethods: [
			{ service: OauthServiceType.Vkontakte, isAvailable: false },
			{ service: OauthServiceType.Google, isAvailable: false },
			{ service: OauthServiceType.Facebook, isAvailable: false },
			{ service: OauthServiceType.TinkoffID, isAvailable: false },
			{ service: OauthServiceType.Telegram, isAvailable: false },
			{ service: OauthServiceType.SberID, isAvailable: false },
			{ service: OauthServiceType.AlfaID, isAvailable: false }
		],
		authMethods: {
			phone: false,
			ffp: true,
			email: true,
			blockedRegistrationEmailDomains: ['gmail.com', 'icloud.com']
		},
		showIsActualizationResultOk: true,
		allowPromoCodes: false,
		i18n: {},
		findOrderURL: '',
		seatMap: {
			splitByCabin: false,
			showRecommendedSeats: false,
			showFilters: false,
			showPriceInSeat: false,
			collapseEmptySeats: false
		},
		insurance: {
			upsaleStrategy: ['checkout-order-service'],
			allowImplicitConfirm: false,
			multiplePriorityInsurances: false,
			insurancePriorities: []
		},
		reserveEngines: {
			siteUrl: ''
		},
		companyInfo: {
			loyaltyProgramName: 'Websky Club',
			iataCode: 'ZZ',
			ticketNumberCode: '00Z',
			exarePrivacyPolicyURL: 'https://google.com',
			exareRulesURL: 'https://google.com',
			subsidyFaresConditionURL: 'https://websky.aero/',
			transferM2ConditionURL: null,
			loyaltyProgramRegistrationUrl: '',
			loyaltyProgramAccountUrl: null,
			farelockURL: '',
			refundOptionURL: '',
			loyalty: {
				links: [
					{
						title: 'Statuses',
						url: 'https://websky.tech'
					},
					{
						title: 'Earn',
						url: 'https://websky.tech'
					},
					{
						title: 'Use',
						url: 'https://websky.tech'
					},
					{
						title: 'FAQ',
						url: 'https://websky.tech'
					}
				]
			},
			multiInsurance: true,
			specialDocumentForInsurance: true
		},
		servicesAvailable: {
			animal: false
		},
		stepsAvailable: {
			[StepsEnum.Meal]: true
		},
		FFPRegex: /^\d{6}$/,
		roundMoneyAmount: false,
		purchasePredictionThreshold: 0.6,
		telegramLoginBotId: null,
		socialAuthCallbackUrl: null,
		showLeavePageModal: false,
		showCitiesPlaceholder: false,
		isOldConfirmOption: false,
		splitCartFlightPassengers: false,
		fareGroupVariant: 'small',
		showBookingPreview: true,
		collectMetrics: true,
		useYandexMetrika: true,
		useSberAds: true,
		showFareGroupUpgradeOptions: true,
		usePixel: false,
		showTaxesInfo: false,
		alternativeRouting: false
	},
	validate: function () {
		return ConfigSchema.safeParse(this).error?.errors || [];
	}
};

const mergeAuthMethods = (
	defaultConfig: Config,
	overrideConfig?: Partial<Config>
): Config['global']['authMethods'] => ({
	...defaultConfig.global.authMethods,
	blockedRegistrationEmailDomains:
		overrideConfig?.global?.authMethods?.blockedRegistrationEmailDomains ??
		defaultConfig.global.authMethods?.blockedRegistrationEmailDomains
});

export const mergeConfig = (defaultConfig: Config, initConfig: Config): Config => {
	const mergedConfig = merge({}, defaultConfig, initConfig);

	if (mergedConfig.SearchForm?.selectableCurrencies) {
		mergedConfig.SearchForm.selectableCurrencies =
			initConfig?.SearchForm?.selectableCurrencies ?? defaultConfig?.SearchForm?.selectableCurrencies;
	}

	mergedConfig.global.authMethods = mergeAuthMethods(mergedConfig, initConfig);

	return mergedConfig;
};

export const applyConfig = (config: Partial<Config>) => {
	if (config?.global?.i18n) {
		extendI18n(config.global.i18n);
	}

	if (config?.global?.apiUrl) {
		setApiUrl(config.global.apiUrl);
	}

	if (config?.global?.socialAuthCallbackUrl) {
		config.global.socialAuthCallbackUrl = removeTrailingSlash(config.global.socialAuthCallbackUrl);
	}

	const mergedConfig = merge({}, defaultConfig, config);

	/**
	 * WEBSKY-7938: collectMetrics является общим рубильником для всех метрик
	 * Если collectMetrics = false, принудительно устанавливаем:
	 */
	if (mergedConfig.global.collectMetrics === false) {
		mergedConfig.global.usePixel = false;
		mergedConfig.global.useYandexMetrika = false;
		mergedConfig.global.useSberAds = false;
	}

	mergedConfig.global.additionalResources.extraScripts = [
		...new Set([
			...(defaultConfig.global?.additionalResources?.extraScripts || []),
			...(config.global?.additionalResources?.extraScripts || [])
		])
	];
	mergedConfig.global.additionalResources.extraSvgs = [
		...new Set([
			...(defaultConfig.global?.additionalResources?.extraSvgs || []),
			...(config.global?.additionalResources?.extraSvgs || [])
		])
	];
	const rawDefaultSteps = config?.Engine?.defaultSteps || defaultConfig.Engine.defaultSteps;
	const servicesStepsOrder = config?.Engine?.servicesStepsOrder ?? defaultConfig.Engine?.servicesStepsOrder;

	mergedConfig.Engine.defaultSteps = applyServicesStepsOrderToDefaultSteps(rawDefaultSteps, servicesStepsOrder);
	mergedConfig.global.authMethods = mergeAuthMethods(mergedConfig, config);

	if (mergedConfig.global.companyInfo.iataCode !== 'ZZ') {
		const validationErrors = mergedConfig.validate();

		if (validationErrors.length > 0) {
			const fields = validationErrors.map(error => error.path.join('.'));
			const message = `Invalid config, fields: ${fields.join(', ')}`;

			console.error(message, validationErrors);
			captureErrorSentry(message);
		}
	}

	window.__webskyEngineConfig = Object.freeze({ ...mergedConfig });
	window['__webskyChangeLocale'] = (locale: Locale) => {
		changeLocale(locale).then(() => window.location.reload());
	};

	window['__webskyCoreVersion'] = process.env.PACKAGE_VERSION;

	return mergedConfig;
};

export const ConfigContext = React.createContext<Config>(defaultConfig);

export const useConfig = () => useContext(ConfigContext);

export const ModeContext = React.createContext<Mode>(Mode.Booking);

export const useIsMode = (mode: Mode) => {
	return useContext(ModeContext) === mode;
};

export const useIsCheckout = () => {
	return useIsMode(Mode.Booking);
};

export const useIsCheckin = () => {
	return useIsMode(Mode.Checkin);
};
